import React from 'react';
import {
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination
} from '@mui/material';
import EmptyState from '../EmptyState';
import { formatCurrency, formatNumber, formatQuantity } from './formatters';

interface TransactionDetail {
  cfop: string;
  dt_emissao: string;
  num_doc: string;
  vl_icms: number;
  qtd: number;
  vl_item: number;
  ind_oper: string;
}

interface DrillDownDetailedTransactionsProps {
  transacoes_detalhadas: TransactionDetail[];
  title?: string;
  showPagination?: boolean;
}

const DrillDownDetailedTransactions: React.FC<DrillDownDetailedTransactionsProps> = ({ 
  transacoes_detalhadas, 
  title = "Transações Detalhadas (até 20 registros)",
  showPagination = true
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const transacoes = transacoes_detalhadas || [];

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
        {title}
        {transacoes.length > 0 && (
          <Chip 
            label={`${formatNumber(transacoes.length)} transações`} 
            size="small" 
            sx={{ ml: 1 }} 
            color="primary"
            variant="outlined"
          />
        )}
      </Typography>
      
      {transacoes.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell><strong>Documento</strong></TableCell>
                  <TableCell><strong>CFOP</strong></TableCell>
                  <TableCell align="right"><strong>Quantidade</strong></TableCell>
                  <TableCell align="right"><strong>Valor Item</strong></TableCell>
                  <TableCell align="right"><strong>ICMS</strong></TableCell>
                  <TableCell align="center"><strong>Tipo</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(showPagination ? 
                  transacoes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : 
                  transacoes
                ).map((transacao, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {new Date(transacao.dt_emissao).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>{transacao.num_doc}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transacao.cfop}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatQuantity(transacao.qtd || 0)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary.main" fontWeight={500}>
                        {formatCurrency(transacao.vl_item)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary.main">
                        {formatCurrency(transacao.vl_icms)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={transacao.ind_oper === '0' ? 'Entrada' : 'Saída'}
                        color={transacao.ind_oper === '0' ? 'success' : 'warning'}
                        variant="filled"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {showPagination && transacoes.length > rowsPerPage && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={transacoes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
            />
          )}
        </>
      ) : (
        <EmptyState
          icon="📄"
          title="Nenhuma transação encontrada"
          description="Este produto não possui transações registradas"
        />
      )}
    </Grid>
  );
};

export default DrillDownDetailedTransactions; 